import { isValid, parseISO } from 'date-fns';
import i18n from '../../i18n/i18n';
import {
  formatDate as formatLocalizedDate,
  formatDateTime as formatLocalizedDateTime,
  formatRelativeTime as formatLocalizedRelativeTime,
} from '../../i18n/formatters';
import {
  getCompanyRole,
  getMonitoringPropertyLabel,
  getMonitoringPropertyValue,
  getMonitoringRuleName,
} from '../../i18n/backendTranslations';
import {
  GraphMonitoringDiff,
  GraphMonitoringDiffPayload,
  GraphMonitoringEntityDetail,
  GraphMonitoringEntitySummary,
  GraphMonitoringSnapshot,
  GraphMonitoringSnapshotNode,
  GraphMonitoringSnapshotRelationship,
} from './types';

export function parseDateTime(value?: string | null): Date | null {
  if (!value) {
    return null;
  }

  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : null;
}

export function formatDateTime(value?: string | null): string {
  const parsed = parseDateTime(value);
  return formatLocalizedDateTime(parsed, i18n.resolvedLanguage) ?? i18n.t('common.states.unknown');
}

export function formatDate(value?: string | null): string {
  const parsed = parseDateTime(value);
  return formatLocalizedDate(parsed, i18n.resolvedLanguage) ?? i18n.t('common.states.unknown');
}

export function formatRelativeTime(value?: string | null): string | null {
  const parsed = parseDateTime(value);
  return formatLocalizedRelativeTime(parsed, i18n.resolvedLanguage);
}

export function humanizeValue(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

export function isBadMonitoringDisplayName(value?: string | null): boolean {
  const trimmed = value?.trim();

  if (!trimmed) {
    return true;
  }

  return trimmed.toLowerCase() === 'disabled';
}

export function resolveMonitoringDisplayName(
  ...candidates: Array<string | null | undefined>
): string {
  for (const candidate of candidates) {
    if (!isBadMonitoringDisplayName(candidate)) {
      return candidate!.trim();
    }
  }

  return '';
}

export function formatNodeType(nodeType?: string | null): string {
  if (!nodeType) return i18n.t('common.states.unknown');
  const key = `monitoring.nodeTypes.${nodeType}`;
  return i18n.exists(key) ? i18n.t(key) : humanizeValue(nodeType);
}

export function formatRelationshipType(type?: string | null): string {
  if (!type) {
    return i18n.t('common.states.unknown');
  }

  const key = `monitoring.relationshipTypes.${type}`;
  return i18n.exists(key) ? i18n.t(key) : humanizeValue(type);
}

export function formatRuleMatch(rule?: string | null): string {
  if (!rule) {
    return i18n.t('monitoring.unknownRule');
  }

  return getMonitoringRuleName(rule, humanizeValue(rule));
}

export function formatNotificationStatus(status?: string | null): string {
  return status ? humanizeValue(status) : i18n.t('common.states.unknown');
}

export function getEntityDisplayName(
  entity?: Pick<GraphMonitoringEntitySummary, 'displayName' | 'externalId'> | null,
): string {
  if (!entity) {
    return i18n.t('monitoring.unknownCompany');
  }

  return resolveMonitoringDisplayName(entity.displayName, entity.externalId) || entity.externalId;
}

export function getSnapshotMonitoredEntityDisplayName(
  snapshot?: GraphMonitoringSnapshot | null,
): string {
  if (!snapshot) {
    return '';
  }

  const monitoredEntity = snapshot.snapshot?.monitored_entity;
  const snapshotDisplayName = resolveMonitoringDisplayName(monitoredEntity?.display_name);

  if (snapshotDisplayName) {
    return snapshotDisplayName;
  }

  const externalId = monitoredEntity?.external_id;

  if (!externalId) {
    return '';
  }

  const matchingNode = Object.values(snapshot.snapshot?.nodes ?? {}).find(
    (node) => node.node_type === 'Company' && node.external_id === externalId,
  );

  if (!matchingNode) {
    return '';
  }

  const nodeLabel = getNodeDisplayLabel(matchingNode);
  return nodeLabel !== externalId ? nodeLabel : '';
}

export function getDetailDisplayName(
  detail: Pick<GraphMonitoringEntityDetail, 'monitoredEntity' | 'snapshots'>,
  preferredDisplayName?: string,
): string {
  const latestSnapshot =
    detail.snapshots.find((snapshot) => snapshot.isCurrent) ??
    [...detail.snapshots].sort((left, right) => right.capturedAt.localeCompare(left.capturedAt))[0] ??
    null;

  return (
    resolveMonitoringDisplayName(
      detail.monitoredEntity.displayName,
      getSnapshotMonitoredEntityDisplayName(latestSnapshot),
      preferredDisplayName,
      detail.monitoredEntity.externalId,
    ) || detail.monitoredEntity.externalId
  );
}

export function stringifyValue(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  if (Array.isArray(value)) {
    return value.length > 0 ? value.map(stringifyValue).join(', ') : '—';
  }

  if (typeof value === 'boolean') {
    return i18n.t(value ? 'common.states.yes' : 'common.states.no');
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
}

export function stringifyMonitoringPropertyValue(propertyName: string, value: unknown): string {
  return getMonitoringPropertyValue(propertyName, value) ?? stringifyValue(value);
}

export function getPropertyEntries(properties?: Record<string, unknown> | null) {
  if (!properties) {
    return [];
  }

  return Object.entries(properties).sort(([a], [b]) => a.localeCompare(b, 'sv'));
}

export function getSnapshotNodes(
  snapshot?: GraphMonitoringSnapshot | null,
): GraphMonitoringSnapshotNode[] {
  return snapshot?.snapshot?.nodes ? Object.values(snapshot.snapshot.nodes) : [];
}

export function getSnapshotRelationships(
  snapshot?: GraphMonitoringSnapshot | null,
): GraphMonitoringSnapshotRelationship[] {
  return snapshot?.snapshot?.relationships
    ? Object.values(snapshot.snapshot.relationships)
    : [];
}

function getAddressDisplayLabel(properties: Record<string, unknown>): string | null {
  const street = typeof properties.street === 'string' ? properties.street.trim() : '';
  const postalCode =
    typeof properties.postal_code === 'string' ? properties.postal_code.trim() : '';
  const city = typeof properties.postal_address === 'string' ? properties.postal_address.trim() : '';

  const locality = [postalCode, city].filter(Boolean).join(' ');
  const parts = [street, locality].filter(Boolean);

  return parts.length > 0 ? parts.join(', ') : null;
}

export function getNodeDisplayLabel(node: GraphMonitoringSnapshotNode): string {
  const properties = node.properties ?? {};

  switch (node.node_type) {
    case 'Company':
      return stringifyValue(properties.organization_name) !== '—'
        ? stringifyValue(properties.organization_name)
        : node.external_id;
    case 'FunktionarPerson':
      return stringifyValue(properties.funktionar_namn) !== '—'
        ? stringifyValue(properties.funktionar_namn)
        : node.external_id;
    case 'CarRegNumber':
      return stringifyValue(properties.car_reg_number) !== '—'
        ? stringifyValue(properties.car_reg_number)
        : node.external_id;
    case 'Case':
      return stringifyValue(properties.case_id) !== '—'
        ? stringifyValue(properties.case_id)
        : node.external_id;
    case 'Address': {
      const addressLabel = getAddressDisplayLabel(properties);
      return addressLabel ?? node.external_id;
    }
    default:
      return node.external_id;
  }
}

export function getNodeSecondaryLabel(node: GraphMonitoringSnapshotNode): string | null {
  const label = getNodeDisplayLabel(node);
  return label !== node.external_id ? node.external_id : null;
}

export function getRelationshipDisplayLabel(
  relationship: GraphMonitoringSnapshotRelationship,
): string {
  if (
    relationship.relationship_type === 'FUNKTIONAR' &&
    relationship.properties?.funktionar_funk_text
  ) {
    return `${formatRelationshipType(relationship.relationship_type)}: ${getCompanyRole(
      String(relationship.properties.funktionar_funk_text),
    )}`;
  }

  return formatRelationshipType(relationship.relationship_type);
}

export function buildGraphTooltipContent(
  title: string,
  properties?: Record<string, unknown>,
): string {
  const rows = getPropertyEntries(properties)
    .map(
      ([key, value]) =>
        `<tr><td style="padding:2px 8px 2px 0;"><strong>${getMonitoringPropertyLabel(
          key,
        )}</strong></td><td style="padding:2px 0;">${stringifyMonitoringPropertyValue(key, value)}</td></tr>`,
    )
    .join('');

  return `<div style="font-family:Inter, sans-serif; padding:8px 10px;"><div style="font-weight:600; margin-bottom:6px;">${title}</div><table>${rows}</table></div>`;
}

export function diffHasAnyChanges(diffPayload?: GraphMonitoringDiffPayload | null): boolean {
  if (!diffPayload) {
    return false;
  }

  return (
    diffPayload.added_nodes.length > 0 ||
    diffPayload.removed_nodes.length > 0 ||
    diffPayload.changed_nodes.length > 0 ||
    diffPayload.added_relationships.length > 0 ||
    diffPayload.removed_relationships.length > 0 ||
    diffPayload.changed_relationships.length > 0
  );
}

export function getDefaultSnapshot(
  snapshots: GraphMonitoringSnapshot[],
  selectedSnapshotId?: string | null,
): GraphMonitoringSnapshot | null {
  if (snapshots.length === 0) {
    return null;
  }

  return (
    snapshots.find((snapshot) => snapshot.id === selectedSnapshotId) ||
    snapshots.find((snapshot) => snapshot.isCurrent) ||
    snapshots[0]
  );
}

export function getDefaultDiff(
  diffs: GraphMonitoringDiff[],
  selectedDiffId?: string | null,
): GraphMonitoringDiff | null {
  if (diffs.length === 0) {
    return null;
  }

  return diffs.find((diff) => diff.id === selectedDiffId) || diffs[0];
}
